import { z } from 'zod'

/**
 * The media vocabulary: what kind of coverage a hit is, in Headline's own words, and the one
 * translation from the analytics backend's. Its own module because the stream, the hit detail and
 * the entitlement catalogue all decide on it, and none of them owns it.
 */

export const MEDIA_TYPES = ['news', 'broadcast', 'podcast', 'social', 'newsletter'] as const
export const mediaTypeSchema = z.enum(MEDIA_TYPES)
export type MediaType = z.infer<typeof mediaTypeSchema>

/**
 * The analytics backend's article types (`NEWS`, `BROADCAST`, … — uppercase on the cached rows
 * and on `/v1/hit/:id` alike) in the stream's media vocabulary. The one translation between the
 * two: anything Headline decides on a media type (the full-text licence included) decides on the
 * mapped value, never on the backend's raw string. Unknown or absent is null.
 */
const ARTICLE_TYPE_MEDIA: Record<string, MediaType> = {
  NEWS: 'news',
  ACADEMIC: 'news',
  BROADCAST: 'broadcast',
  PODCAST: 'podcast',
  SOCIAL: 'social',
  SOCIAL_MEDIA: 'social',
  NEWSLETTER: 'newsletter',
}

export function mediaTypeOfArticleType(articleType: string | null | undefined): MediaType | null {
  return (articleType && ARTICLE_TYPE_MEDIA[articleType.toUpperCase()]) || null
}

/** Spoken media carry a transcript rather than a written body. */
export function isSpokenMedia(mediaType: MediaType | null): boolean {
  return mediaType === 'broadcast' || mediaType === 'podcast'
}

export const MEDIA_TYPE_LABELS: Record<MediaType, string> = {
  news: 'News',
  broadcast: 'Broadcast',
  podcast: 'Podcast',
  social: 'Social',
  newsletter: 'Newsletter',
}
